import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { 
  Search, BookOpen, Plus, Phone, Mail, Trash2, Loader2, X, 
  Filter, CheckCircle, Upload, AlertCircle, Maximize2, Tag 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI SICUREZZA
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_TITLE_LENGTH = 100;
const MAX_DESC_LENGTH = 1000;
const MAX_CONTACT_LENGTH = 100;
const ADMIN_EMAIL = 'info@tusciahub.it'; 

const Marketplace = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('Tutti');
  const departments = ['Tutti', 'DEIM', 'DISTU', 'DISUCOM', 'DAFNE', 'DIBAF', 'DEB'];
  const conditions = ['Come Nuovo', 'Ottimo', 'Buono', 'Sottolineato', 'Usato'];

  // Stati Modali
  const [isSellOpen, setIsSellOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState(false);
  
  const [toast, setToast] = useState(null);

  const [newItem, setNewItem] = useState({ 
    title: '', description: '', price: '', department: 'DEIM', 
    condition: 'Come Nuovo', contact_info: '', contact_type: 'whatsapp', 
    image: null, termsAccepted: false 
  });

  useEffect(() => {
    const init = async () => {
        setLoading(true);
        await checkUser();
        await fetchItems();
        setLoading(false);
    };
    init();
  }, []);

  // --- FIX SCROLL ---
  useEffect(() => {
    if (isSellOpen || selectedItem) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isSellOpen, selectedItem]);

  const checkUser = async () => { 
      const { data: { user } } = await supabase.auth.getUser(); 
      setUser(user); 
      if (user && user.email === ADMIN_EMAIL) {
          setIsAdmin(true);
      }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('marketplace_items')
        .select('*, profiles(email)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) { 
      console.error(error); 
    }
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
          showToast("Solo immagini (JPG, PNG).", "error");
          e.target.value = null;
          return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(true);
          setNewItem({ ...newItem, image: null });
          e.target.value = null;
      } else {
          setFileError(false);
          setNewItem({ ...newItem, image: file });
      }
  };

  const handleSellItem = async (e) => {
    e.preventDefault(); 
    if (!user) return showToast("Devi accedere per vendere!", "error");
    if (!newItem.termsAccepted) return showToast("Accetta i termini.", "error");
    if (!newItem.image) return showToast("Foto obbligatoria!", "error");
    if (fileError) return showToast("File troppo grande.", "error");
    
    if (newItem.title.length > MAX_TITLE_LENGTH) return showToast("Titolo troppo lungo.", "error");
    if (newItem.description.length > MAX_DESC_LENGTH) return showToast("Descrizione troppo lunga.", "error");
    if (newItem.contact_info.length > MAX_CONTACT_LENGTH) return showToast("Contatto troppo lungo.", "error");

    setIsSubmitting(true);
    
    try {
      const fileExt = newItem.image.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}_book.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('market_images')
        .upload(fileName, newItem.image);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('market_images')
        .getPublicUrl(fileName);
      
      const { error: dbError } = await supabase.from('marketplace_items').insert([{ 
        title: newItem.title, description: newItem.description, 
        price: parseFloat(newItem.price), department: newItem.department, 
        condition: newItem.condition, contact_info: newItem.contact_info, 
        contact_type: newItem.contact_type, image_url: publicUrl, 
        user_id: user.id 
      }]);

      if (dbError) throw dbError;
      
      showToast("Libro messo in vendita!", "success");
      setIsSellOpen(false); 
      setNewItem({ title: '', description: '', price: '', department: 'DEIM', condition: 'Come Nuovo', contact_info: '', contact_type: 'whatsapp', image: null, termsAccepted: false });
      setFileError(false);
      await fetchItems();

    } catch (error) { showToast("Errore: " + error.message, "error"); } 
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (itemId, imageUrl) => { 
      if(!window.confirm("Sicuro di voler eliminare?")) return; 
      
      try { 
          if (imageUrl) {
              const fileUrlParts = imageUrl.split('/market_images/');
              if (fileUrlParts.length > 1) {
                  const filePath = fileUrlParts[1];
                  const { error: storageError } = await supabase.storage
                      .from('market_images') 
                      .remove([filePath]);
                  if (storageError) console.error("Errore cancellazione file:", storageError);
              }
          }

          const { error } = await supabase.from('marketplace_items').delete().eq('id', itemId);
          if (error) throw error;

          setItems(items.filter(i => i.id !== itemId)); 
          showToast("Annuncio eliminato.", "success");
          if(selectedItem?.id === itemId) setSelectedItem(null); 

      } catch(err) { showToast("Errore cancellazione.", "error"); } 
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = selectedDept === 'Tutti' || item.department === selectedDept;
    return matchesSearch && matchesDept;
  });

  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-300 outline-none transition-all shadow-sm";

  if (loading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-100 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title="Mercatino Libri" description="Compra e vendi libri universitari." url="/mercatino" />
      {/* Sfondo Decorativo ORANGE */}
      <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-orange-100/60 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      
      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[320px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-800' : 'bg-slate-900 text-white border-slate-800'}`}>
            {toast.type === 'error' ? <AlertCircle className="text-red-400"/> : <CheckCircle className="text-green-400"/>}
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader title="Mercatino Libri" subtitle="Trova i testi per i tuoi esami." breadcrumbs={[{label: 'Libri'}]} />

        {/* --- BARRA FILTRI --- */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-10">
            <div className="flex flex-wrap xl:flex-nowrap gap-6 items-center justify-between">
                
                <div className="flex flex-col md:flex-row gap-6 w-full xl:w-auto flex-grow min-w-0 items-center">
                    <div className="relative w-full md:w-[320px] shrink-0 group">
                        <input type="text" placeholder="Es. Manuale di Diritto..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-12 pr-4 font-bold outline-none focus:ring-2 focus:ring-orange-400 focus:bg-white transition-all text-sm group-hover:border-slate-300" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-orange-500 transition-colors" size={20}/>
                    </div>

                    <div className="flex-1 w-full md:w-auto overflow-x-auto pb-4 custom-scrollbar min-w-0">
                        <div className="flex gap-2">
                            {departments.map(d => (
                                <button key={d} onClick={() => setSelectedDept(d)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition border ${selectedDept === d ? 'bg-orange-500 text-white border-orange-500 shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:border-orange-200 hover:text-orange-600'}`}>{d}</button>
                            ))}
                        </div>
                    </div>
                </div>

                <button 
                    onClick={() => user ? setIsSellOpen(true) : showToast("Accedi per vendere!", "error")} 
                    className="w-full xl:w-auto bg-orange-500 hover:bg-orange-600 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0 mt-2 xl:mt-0"
                >
                    <Plus size={20}/> Vendi Libro
                </button>
            </div>
        </div>

        {/* --- GRIGLIA ITEMS --- */}
        {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredItems.map(item => (
                    // CARD ORANGE SFUMATA
                    <div key={item.id} onClick={() => setSelectedItem(item)} className="group bg-gradient-to-b from-white to-orange-50 border-2 border-orange-100 rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl hover:border-orange-300 transition hover:-translate-y-2 duration-300 flex flex-col cursor-pointer relative">
                        
                        <div className="h-64 bg-slate-100 relative overflow-hidden flex items-center justify-center p-6">
                            <img src={item.image_url} className="w-auto h-full max-w-full object-contain shadow-md group-hover:scale-105 transition duration-500 rounded-md" alt={item.title}/>
                            
                            <span className="absolute top-4 right-4 bg-white/90 backdrop-blur text-slate-900 text-[10px] font-black px-2 py-1 rounded-lg shadow-sm border border-slate-100">{item.condition}</span>
                            <span className="absolute bottom-4 left-4 bg-slate-900 text-white text-[10px] font-black px-2 py-1 rounded-lg shadow-lg">{item.department}</span>
                            
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="bg-white/90 text-slate-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                    <Maximize2 size={16}/> Vedi
                                </span>
                            </div>
                        </div>

                        <div className="p-5 flex flex-col flex-grow">
                            {/* BOX PREZZO VIBRANTE (Arancio) */}
                            <div className="mb-4">
                                <div className="w-full bg-gradient-to-r from-orange-500 to-amber-600 p-3 rounded-xl text-white shadow-lg shadow-orange-200/50 flex items-center justify-center">
                                    <span className="text-xl font-black tracking-tight">€{item.price}</span>
                                </div>
                            </div>

                            <h3 className="font-bold text-slate-800 text-lg leading-tight mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                            <p className="text-slate-500 text-xs line-clamp-2 mb-4 flex-grow">{item.description}</p>
                            
                            <div className="mt-auto pt-4 border-t border-orange-100 flex justify-between items-center">
                                <span className="text-xs font-bold text-orange-600 group-hover:underline flex items-center gap-1">
                                    <Tag size={12}/> Dettagli
                                </span>
                                {user && (user.id === item.user_id || isAdmin) && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.image_url); }} 
                                        className="bg-white text-red-500 p-2 rounded-lg hover:bg-red-50 transition z-20 border border-red-100 shadow-sm"
                                    >
                                        <Trash2 size={16}/>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-24 bg-white border border-dashed border-slate-200 rounded-[3rem]">
                <div className="bg-orange-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-orange-400 animate-pulse"><BookOpen size={32}/></div>
                <h3 className="font-bold text-slate-900 text-lg">Nessun libro trovato</h3>
                <p className="text-slate-400 text-sm mb-6">Non ci sono libri in vendita con questi filtri.</p>
                <button onClick={() => user ? setIsSellOpen(true) : showToast("Accedi prima!", "error")} className="text-orange-600 font-bold bg-white border border-orange-200 px-6 py-2 rounded-full hover:bg-orange-50 transition shadow-sm">Vendi il primo libro!</button>
            </div>
        )}

        {/* --- MODALE DETTAGLIO --- */}
        {selectedItem && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedItem(null)}></div>
                <div className="relative z-10 bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-300">
                    <button onClick={() => setSelectedItem(null)} className="absolute top-4 right-4 z-10 p-2 bg-white/50 backdrop-blur rounded-full hover:bg-white text-slate-800 transition shadow-sm"><X size={24}/></button>
                    
                    <div className="w-full md:w-1/2 h-64 md:h-auto bg-slate-100 relative flex items-center justify-center p-10">
                        <img src={selectedItem.image_url} className="w-auto h-full max-w-full object-contain shadow-2xl" alt={selectedItem.title}/>
                        <div className="absolute bottom-6 left-6 flex gap-2">
                            <span className="bg-white text-slate-900 px-3 py-1 rounded-lg text-xs font-black uppercase shadow-md">{selectedItem.condition}</span>
                            <span className="bg-slate-900 text-white px-3 py-1 rounded-lg text-xs font-black uppercase shadow-md">{selectedItem.department}</span>
                        </div>
                    </div>

                    <div className="w-full md:w-1/2 p-8 overflow-y-auto bg-white flex flex-col custom-scrollbar">
                        <div className="flex items-center gap-2 text-slate-400 text-sm font-bold mb-2">Pubblicato il {new Date(selectedItem.created_at).toLocaleDateString()}</div>
                        <h2 className="text-3xl font-black text-slate-900 leading-tight mb-2">{selectedItem.title}</h2>
                        <div className="text-3xl font-black text-orange-600 mb-6">€{selectedItem.price}</div>
                        <div className="prose prose-slate prose-sm text-slate-600 mb-8 leading-relaxed"><p className="whitespace-pre-wrap">{selectedItem.description}</p></div>
                        <div className="mt-auto space-y-3">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Contatta Venditore</p>
                            <a href={selectedItem.contact_type === 'whatsapp' ? `https://wa.me/${selectedItem.contact_info}` : `mailto:${selectedItem.contact_info}`} target="_blank" rel="noopener noreferrer" className={`w-full text-white py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition shadow-xl hover:-translate-y-1 hover:shadow-orange-200 active:scale-95 ${selectedItem.contact_type === 'whatsapp' ? 'bg-green-500 hover:bg-green-600' : 'bg-slate-900 hover:bg-slate-800'}`}>{selectedItem.contact_type === 'whatsapp' ? <Phone size={24}/> : <Mail size={24}/>} {selectedItem.contact_type === 'whatsapp' ? 'Scrivigli su WhatsApp' : 'Invia una Email'}</a>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- MODALE UPLOAD (Stile Housing, Colori Orange) --- */}
        {isSellOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsSellOpen(false)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-lg rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 border border-white/20 p-8">
                    
                    <button onClick={() => setIsSellOpen(false)} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>
                    
                    <div className="mb-6 pr-10">
                        <h3 className="text-2xl font-black text-slate-900 mb-2">Vendi Libro</h3>
                        <p className="text-slate-500 text-sm mb-6 font-medium">Inserisci i dettagli del libro da vendere.</p>
                    </div>
                    
                    <div className="overflow-y-auto custom-scrollbar flex-1 -mr-4 pr-4">
                        <form onSubmit={handleSellItem} className="space-y-4 pb-2">
                            <div className="space-y-1">
                                <input type="text" required maxLength={MAX_TITLE_LENGTH} className={inputStyle} placeholder="Titolo" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative">
                                    <input type="number" required min="0" step="0.01" className={inputStyle} placeholder="Prezzo €" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                                </div>
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="w-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer" value={newItem.department} onChange={e => setNewItem({...newItem, department: e.target.value})}>
                                        {departments.filter(d=>d!=='Tutti').map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="w-full bg-transparent px-3 text-sm font-bold outline-none text-slate-600 cursor-pointer" value={newItem.condition} onChange={e => setNewItem({...newItem, condition: e.target.value})}>
                                        {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="bg-slate-100 text-xs font-bold px-1 border-r border-slate-200 outline-none text-slate-500 cursor-pointer" value={newItem.contact_type} onChange={e => setNewItem({...newItem, contact_type: e.target.value})}>
                                        <option value="whatsapp">WA</option>
                                        <option value="email">@</option>
                                    </select>
                                    <input type="text" required maxLength={MAX_CONTACT_LENGTH} className="w-full bg-transparent px-3 text-sm font-medium outline-none placeholder:text-slate-400" placeholder="Contatto" value={newItem.contact_info} onChange={e => setNewItem({...newItem, contact_info: e.target.value})} />
                                </div>
                            </div>

                            <textarea className={`${inputStyle} h-24 resize-none`} placeholder="Note..." maxLength={MAX_DESC_LENGTH} value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})}></textarea>
                            
                            <div className="relative group">
                                <input type="file" accept="image/*" className="hidden" id="img-upload-book" onChange={handleFileChange} />
                                <label htmlFor="img-upload-book" className={`flex flex-col items-center justify-center h-20 w-full border-2 border-dashed rounded-2xl transition-all group-hover:scale-[1.01] cursor-pointer ${fileError ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-orange-300'}`}>
                                    {newItem.image ? (
                                        <div className="flex flex-col items-center text-orange-600 animate-in zoom-in">
                                            <CheckCircle size={24} className="mb-1"/>
                                            <span className="text-xs font-bold">{newItem.image.name}</span>
                                        </div>
                                    ) : (
                                        <span className="text-orange-500 font-bold text-sm">{fileError ? "File troppo grande" : "Carica Copertina"}</span>
                                    )}
                                </label>
                            </div>

                            <label className="flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 rounded-lg transition">
                                <input type="checkbox" required className="w-4 h-4 rounded text-orange-600 focus:ring-orange-500 cursor-pointer" checked={newItem.termsAccepted} onChange={e => setNewItem({...newItem, termsAccepted: e.target.checked})}/>
                                <span className="text-xs text-slate-500 font-medium">Accetto i <Link to="/terms" className="text-orange-600 font-bold hover:underline" target="_blank">Termini e Condizioni</Link>.</span>
                            </label>

                            <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition shadow-xl shadow-slate-200 active:scale-95 flex items-center justify-center gap-2">
                                {isSubmitting ? <Loader2 className="animate-spin"/> : 'Pubblica'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;